import ReactModal from 'react-modal';
import styled from 'styled-components';
import Button from '../Button';

const Container = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  text-align: left;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
  padding-top: 30px;
`;

const TopRightBox = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  z-index: 90;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
`;

const ContentSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const FooterSection = styled.div`
  flex-shrink: 0;
  padding-top: 10px;
`;

const Checkbox = styled.input`
  margin-right: 4px;
  transform: scale(1.1);
`;

const Label = styled.label`
  font-size: 13px;
  color: #555;
  cursor: pointer;
  user-select: none;
`;

const Title = styled.p`
  font-size: 28px;
  font-weight: 400;
  line-height: 40px;
  margin: 0 0 8px 0;
`;

const Subtitle = styled(Title)`
  font-size: 20px;
  margin-bottom: 16px;
`;

const Paragraph = styled.p`
  font-size: 14px;
  line-height: 2;
`;

const HighlightBox = styled.div`
  background-color: #f1f7ff;
  color: #003173;
  line-height: 1.5;
  margin: 15px 0 16px;
  padding: 16px;
  border-radius: 6px;
  font-size: 14px;
`;

const HighlightItem = styled.div`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
`;

const HighlightItemLabel = styled.span`
  font-weight: 700;
  font-size: 14px;
`;

const HighlightItemValue = styled.span`
  font-size: 14px;
`;

const Link = styled.a`
  color: #2d8cff;
  text-decoration: underline;
  font-size: 14px;
`;

export const Bold = styled.strong`
  font-size: 14px;
`;

const Spacer = styled.div`
  margin: 5px 0px;
  font-size: 14px;
`;

interface NoticeBoxProps {
  /**
   * @alias 모달 표시 여부
   */
  isOpen: boolean;
  /**
   * @alias 모달 닫기 함수
   */
  onClose?: () => void;
  /**
   * @alias 확인 버튼 클릭 시 실행될 함수
   */
  onConfirm?: () => void;
  /**
   * @alias 제목
   */
  title: string;
  /**
   * @alias 부제목
   */
  subtitle?: string;
  /**
   * @alias 내용
   */
  // content: string | string[];
  content: React.ReactNode | React.ReactNode[];
  /**
   * @alias 정보 박스
   */
  infoBox?: {
    /**
     * @alias 정보 박스 아이템
     */
    items: Array<{
      label: string;
      value: string;
    }>;
    /**
     * @alias 경고 메시지
     */
    warning?: string | string[];
    /**
     * @alias 링크
     */
    links?: Array<{
      text: string;
      url: string;
    }>;
  };
  /**
   * @alias 다시는 보지 않기 체크 시 실행되는 함수
   */
  onDontShowAgain?: (checked: boolean) => void;
}

function NoticeBox({ isOpen, onClose, title, subtitle, content, infoBox, onDontShowAgain }: NoticeBoxProps) {
  if (!isOpen) return null;
  const closeModal = () => {
    onClose?.();
  };
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        },
        content: {
          position: 'relative',
          top: 'auto',
          left: 'auto',
          right: 'auto',
          bottom: 'auto',
          width: '600px',
          maxWidth: '90%',
          maxHeight: '90vh',
          padding: onDontShowAgain ? '20px 30px 30px 30px' : '30px',
          borderRadius: '8px',
          border: 'none',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 10000,
          overflow: 'hidden',
        },
      }}
    >
      <Container>
        {onDontShowAgain && (
          <TopRightBox>
            <Checkbox type="checkbox" id="dontShowAgain" onChange={(e) => onDontShowAgain(e.target.checked)} />
            <Label htmlFor="dontShowAgain">다시는 보지 않기</Label>
          </TopRightBox>
        )}

        <HeaderSection>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HeaderSection>

        <ContentSection>
          {Array.isArray(content) ? (
            content.map((line, idx) => (line === '/n' ? <br key={idx} /> : <Paragraph key={idx}>{line}</Paragraph>))
          ) : (
            <Paragraph>{content}</Paragraph>
          )}

          {infoBox && (
            <HighlightBox>
              {infoBox.items.length > 0 && (
                <Spacer>
                  {infoBox.items.map((item, index) => (
                    <HighlightItem key={index}>
                      <HighlightItemLabel>| {item.label}</HighlightItemLabel>
                      <HighlightItemValue>: {item.value}</HighlightItemValue>
                    </HighlightItem>
                  ))}
                  <br />
                </Spacer>
              )}

              {infoBox.warning && infoBox.warning.length > 0 && (
                <>
                  {Array.isArray(infoBox.warning)
                    ? infoBox.warning.map((line, idx) =>
                        line === '/n' ? (
                          <br key={idx} />
                        ) : (
                          <Spacer style={{ color: '#003173' }} key={idx}>
                            {line}
                          </Spacer>
                        ),
                      )
                    : infoBox.warning && <Spacer>{infoBox.warning}</Spacer>}
                  <br />
                </>
              )}

              {infoBox.links && infoBox.links.length > 0 && (
                <>
                  {infoBox.links?.map((link, index) => (
                    <Spacer key={index}>
                      <Link href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.text}
                      </Link>
                    </Spacer>
                  ))}
                </>
              )}
            </HighlightBox>
          )}
        </ContentSection>

        <FooterSection>
          <Spacer style={{ display: 'flex', justifyContent: 'center' }}>
            <Button size="medium" width={500} color="main" onClick={closeModal}>
              확인
            </Button>
          </Spacer>
        </FooterSection>
      </Container>
    </ReactModal>
  );
}

export default NoticeBox;

// example
/**
 *           {isModalOpen && (
            <NoticeBox
              isOpen={isModalOpen}
              onClose={() => {
                modalOnCloseRef.current();
              }}
              title="네이버 스마트스토어"
              subtitle="수수료 체계가 변경됩니다."
              content={[
                <>변화된 커머스 환경에 맞추어 네이버 플러스 스토어 전시 기능 강화와 함께,</>,
                <>스마트스토어 이용약관 및 수수료 정책도 변경될 예정입니다.</>,
                <>플레이오토 솔루션 사용자 판매자님께서는 스마트스토어 공지사항에서</>,
                <>
                  변경 내용을 확인 후, <Bold>이용약관 동의</Bold>를 진행해 주세요.
                </>,
              ]}
              infoBox={{
                items: [
                  { label: '신규 수수료 적용일', value: '2025년 6월 2일' },
                  { label: '약관 동의 기간', value: '2025년 3월 4일 ~ 6월 1일' },
                ],
                warning: [
                  '2025년 6월 1일까지 동의하지 않으시면',
                  '스마트스토어 이용(상품 노출, 전시, 구매)이 제한되며,',
                  '플레이오토 연결도 중단될 수 있습니다.',
                ],
                links: [
                  {
                    text: '공지사항 바로가기',
                    url: 'https://sell.smartstore.naver.com/#/center-notice/detail/100017611',
                  },
                  {
                    text: '이용약관 동의하러 가기',
                    url: 'https://sell.smartstore.naver.com',
                  },
                ],
              }}
              onDontShowAgain={(checked) => {
                if (checked) {
                  localStorage.setItem('hideSmartstoreNotice', '1');
                } else {
                  localStorage.removeItem('hideSmartstoreNotice');
                }
              }}
            />
          )}
 */
